/**
 * S2 — Agent version history (`/ai-studio/agents/:agentId/versions`).
 * Definition authoring lives at `/ai-studio/agents/:agentId`.
 *
 * New draft versions are created from the Agents catalog (Copy on Private/Public).
 * This page is read-oriented history + agent lifecycle, not the versioning entry point.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { AgentOverview, AgentStateBadge, VersionList } from '@/components/ai-studio/agents';
import {
  getAgent,
  listVersions,
  updateAgent,
} from '@/services/aiStudioAgentService';
import type {
  AiStudioAgent,
  AiStudioAgentVersion,
  StudioAgentState,
} from '@/types/aiStudioAgent';

const AIStudioAgentDetailPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();

  const [agent, setAgent] = useState<AiStudioAgent | null>(null);
  const [versions, setVersions] = useState<AiStudioAgentVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Errors are stored as translation keys so data effects never depend on `t`.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    Promise.all([getAgent(agentId), listVersions(agentId)])
      .then(([loadedAgent, loadedVersions]) => {
        if (cancelled) return;
        setAgent(loadedAgent);
        setVersions(loadedVersions);
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.agents.detail.loadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  const handleStateChange = useCallback(
    async (state: StudioAgentState) => {
      if (!agentId) return;
      setSaving(true);
      setErrorKey(null);
      try {
        setAgent(await updateAgent(agentId, { visibility: state }));
      } catch {
        setErrorKey('aiStudio.agents.detail.saveFailed');
      } finally {
        setSaving(false);
      }
    },
    [agentId]
  );

  const hasDraft = versions.some((v) => v.lifecycleStatus === 'draft');

  if (loading) {
    return (
      <main className="px-4 py-4 md:px-6 lg:px-8">
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.agents.loading')}
        </p>
      </main>
    );
  }

  if (!agent) {
    return (
      <main className="flex flex-col gap-3 px-4 py-4 md:px-6 lg:px-8">
        <p role="alert" className="text-base text-[var(--color-error)]">
          {t(errorKey ?? 'aiStudio.agents.detail.notFound')}
        </p>
        <Link to="/ai-studio/agents" className="w-fit text-base text-[var(--color-info)] underline">
          {t('aiStudio.agents.detail.back')}
        </Link>
      </main>
    );
  }

  return (
    <main className="flex w-full flex-col gap-4 px-4 py-4 md:px-6 lg:px-8">
      <nav>
        <Link to="/ai-studio/agents" className="text-base text-[var(--color-info)] underline">
          {t('aiStudio.agents.detail.back')}
        </Link>
      </nav>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-3 text-lg font-semibold text-[var(--color-surface-text)]">
            <span className="font-mono">{agent.code}</span>
            <AgentStateBadge state={agent.visibility} />
          </h1>
          <p className="text-base text-[var(--color-surface-text-secondary)]">{agent.name}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasDraft && (
            <Link
              to={`/ai-studio/agents/${agent.id}`}
              className="rounded-md border border-[var(--color-border)] px-4 py-2 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)]"
            >
              {t('aiStudio.definition.open')}
            </Link>
          )}
          {!hasDraft && agent.visibility !== 'draft' && (
            <p className="self-center text-base text-[var(--color-surface-text-secondary)]">
              {t('aiStudio.agents.useCatalogCopy')}
            </p>
          )}
        </div>
      </header>

      {errorKey && (
        <div
          role="alert"
          className="rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
        >
          {t(errorKey)}
        </div>
      )}

      <AgentOverview
        agent={agent}
        saving={saving}
        onChangeState={(state) => void handleStateChange(state)}
      />

      <section>
        <h2 className="mb-2 text-base font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.agents.detail.versions')}
        </h2>
        <VersionList
          versions={versions}
          onSelect={(versionId) =>
            navigate(`/ai-studio/agents/${agent.id}/versions/${versionId}`)
          }
        />
      </section>
    </main>
  );
};

export default AIStudioAgentDetailPage;
